if (typeof SizeGuideBraPopup === 'undefined') {
  class SizeGuideBraPopup extends HTMLElement {
    static SELECTORS = {
      backSelect: '[data-bra-back-size]',
      chestSelect: '[data-bra-chest-size]',
      drawer: '[data-size-guide]',
      noMatch: '[data-bra-no-match]',
      optionValue: '.popover__value-option',
      popover: 'x-popover',
      checkedRadio: 'input[type="radio"]:checked',
      outside: 'size-guide-bra'
    };

    static NO_MATCH_CLASS = 'size-guide--no-match';

    static EVENTS = {
      change: 'change',
      input: 'input'
    };

    // Storage shape mirrors `SizeGuideBase` so the two calculators share state.
    // The outside `<size-guide-bra>` reads its recommendation from
    // `state.woman.recommendation.bra`; writing there keeps both UIs in sync.
    static STORAGE = {
      root: 'size-guide-state',
      subKey: 'bra-popup',
      outsideKey: 'woman',
      guideType: 'bra',
      updatedAtKey: '_updatedAt'
    };

    // Mirrors `SizeGuideBase.VARIANT_AJAX_WINDOW_MS` — the window during which
    // the freshly-mounted outside calculator should trust storage over the DOM.
    static RESTORE_WINDOW_MS = 500;

    // Back groups (Gatsby `computedBackSize`): each 10 cm bucket maps to a
    // PERCKO size letter. 68–77 → S, 78–87 → M, 88–97 → L, 98–107 → XL,
    // 108–118 → 2XL. Out of range → no recommendation.
    static BACK_GROUPS = [
      { min: 68, max: 77, size: 'S' },
      { min: 78, max: 87, size: 'M' },
      { min: 88, max: 97, size: 'L' },
      { min: 98, max: 107, size: 'XL' },
      { min: 108, max: 118, size: '2XL' }
    ];

    // Bonnet (cup) thresholds applied to (chest − back). Mirrors Gatsby
    // `computedBonnetSize`: 6–15 → A-B, 16–19 → C-D, 20–23 → E-F, 24–28 → G-H.
    static BONNET_THRESHOLDS = [
      { min: 6,  max: 15, size: 'A-B' },
      { min: 16, max: 19, size: 'C-D' },
      { min: 20, max: 23, size: 'E-F' },
      { min: 24, max: 28, size: 'G-H' }
    ];

    /* ─── Lifecycle ──────────────────────────────────────────────────────── */

    connectedCallback () {
      this._abort = new AbortController();
      // Each picker is an <x-popover> of radios (not a single <select>); capture the
      // popover + its button label so we read the *checked* radio and reflect it.
      this._backPopover = this.querySelector(SizeGuideBraPopup.SELECTORS.backSelect)?.closest(SizeGuideBraPopup.SELECTORS.popover);
      this._chestPopover = this.querySelector(SizeGuideBraPopup.SELECTORS.chestSelect)?.closest(SizeGuideBraPopup.SELECTORS.popover);
      this._backLabel = this._labelFor(this._backPopover);
      this._chestLabel = this._labelFor(this._chestPopover);
      this._drawer = this.closest(SizeGuideBraPopup.SELECTORS.drawer);
      this._noMatch = this._drawer?.querySelector(SizeGuideBraPopup.SELECTORS.noMatch);

      this._restoreFromStorage();
      this._bindEvents();
      this._syncLabels();
      this._refreshNoMatchOnly();
    }

    _labelFor (popover) {
      if (!popover) return null;
      const btn = this.querySelector(`button[aria-controls="${popover.id}"]`);
      return btn?.querySelector(`#${CSS.escape(popover.id)}-selected-value`);
    }

    _pickerValue (popover) {
      return popover?.querySelector(SizeGuideBraPopup.SELECTORS.checkedRadio)?.value;
    }

    // Mirror the checked radio's label (e.g. "80cm") into the button so the chosen
    // value is visible — nothing else updates these picker buttons.
    _syncLabels () {
      this._syncLabel(this._backPopover, this._backLabel);
      this._syncLabel(this._chestPopover, this._chestLabel);
    }

    _syncLabel (popover, labelSpan) {
      const text = popover?.querySelector(SizeGuideBraPopup.SELECTORS.checkedRadio)
        ?.closest(SizeGuideBraPopup.SELECTORS.optionValue)?.textContent.trim();
      if (labelSpan && text) labelSpan.textContent = text;
    }

    // On mount we only sync the visual no-match panel — never re-apply the
    // recommendation to the variant picker. The URL variant is already correct
    // (server-rendered); dispatching here would trigger variant:change and
    // re-render → loop. Apply only fires on real user interaction in _onChange.
    _refreshNoMatchOnly () {
      const back = this._toInt(this._pickerValue(this._backPopover));
      const chest = this._toInt(this._pickerValue(this._chestPopover));
      if (!Number.isFinite(back) || !Number.isFinite(chest)) {
        this._toggleNoMatch(false);
        return;
      }
      const size = this._compute(back, chest);
      this._toggleNoMatch(!size);
      // Only reflect a no-match onto the page on mount; never re-apply a match here
      // (that path dispatches a variant change → re-render loop, see method comment).
      if (!size) this._syncOutsidePage(true);
    }

    disconnectedCallback () {
      this._abort?.abort();
    }

    _bindEvents () {
      // Listen on each popover so a `change` from any radio in the group is caught
      // (the old code listened on a single radio, so only the first option worked).
      [this._backPopover, this._chestPopover].forEach((popover) => {
        popover?.addEventListener(
          SizeGuideBraPopup.EVENTS.change,
          () => this._onChange(),
          { signal: this._abort.signal }
        );
      });
    }

    /* ─── State (localStorage) ───────────────────────────────────────────── */

    _readRoot () {
      try { return JSON.parse(localStorage.getItem(SizeGuideBraPopup.STORAGE.root) || '{}'); } catch { return {}; }
    }

    _writeRoot (root) {
      localStorage.setItem(SizeGuideBraPopup.STORAGE.root, JSON.stringify(root));
    }

    _persistInputs (back, chest) {
      const root = this._readRoot();
      root[SizeGuideBraPopup.STORAGE.subKey] = {
        back_size: Number.isFinite(back) ? back : null,
        chest_size: Number.isFinite(chest) ? chest : null,
        [SizeGuideBraPopup.STORAGE.updatedAtKey]: Date.now()
      };
      this._writeRoot(root);
    }

    // Write to the SAME namespace `<size-guide-bra>` reads from — so the
    // outside calculator picks up our recommendation on its next re-mount
    // (after an AJAX section re-render).
    _persistRecommendation (size) {
      const root = this._readRoot();
      const outside = root[SizeGuideBraPopup.STORAGE.outsideKey] || { selections: {} };
      // Migrate the legacy flat-string `recommendation` shape if present.
      if (!outside.recommendation || typeof outside.recommendation === 'string') {
        outside.recommendation = {};
      }
      outside.recommendation[SizeGuideBraPopup.STORAGE.guideType] = size;
      outside[SizeGuideBraPopup.STORAGE.updatedAtKey] = Date.now();
      root[SizeGuideBraPopup.STORAGE.outsideKey] = outside;
      this._writeRoot(root);
    }

    _restoreFromStorage () {
      const data = this._readRoot()[SizeGuideBraPopup.STORAGE.subKey];
      if (!data) return;
      if (data.back_size != null) this._checkRadio(this._backPopover, data.back_size);
      if (data.chest_size != null) this._checkRadio(this._chestPopover, data.chest_size);
    }

    _checkRadio (popover, value) {
      const radio = popover?.querySelector(`input[type="radio"][value="${value}"]`);
      if (radio) radio.checked = true;
    }

    /* ─── Computation ────────────────────────────────────────────────────── */

    _onChange () {
      this._syncLabels();
      const back = this._toInt(this._pickerValue(this._backPopover));
      const chest = this._toInt(this._pickerValue(this._chestPopover));

      this._persistInputs(back, chest);

      if (!Number.isFinite(back) || !Number.isFinite(chest)) {
        this._toggleNoMatch(false);
        this._syncOutsidePage(false);
        return;
      }

      const size = this._compute(back, chest);
      if (!size) {
        this._clearStoredRecommendation();
        this._toggleNoMatch(true);
        this._syncOutsidePage(true);
        return;
      }

      const matched = this._applyRecommendation(size);
      if (!matched) this._clearStoredRecommendation();
      this._toggleNoMatch(!matched);
      this._syncOutsidePage(!matched);
    }

    // Mirror the no-match state onto the in-page <size-guide-bra> so the product
    // page shows "Taille non disponible" instead of the variant picker placeholder.
    // On a match the picker is driven by `_applyRecommendation` (variant change +
    // re-render), so we only need to clear any leftover fallback here.
    _syncOutsidePage (noMatch) {
      const outside = document.querySelector(SizeGuideBraPopup.SELECTORS.outside);
      if (!outside?.setPageNoSize) return;
      outside.setPageNoSize(noMatch ? (outside.dataset.noSizeText || '') : '');
    }

    _toggleNoMatch (show) {
      if (this._noMatch) this._noMatch.hidden = !show;
      this._drawer?.classList.toggle(SizeGuideBraPopup.NO_MATCH_CLASS, show);
    }

    _clearStoredRecommendation () {
      const root = this._readRoot();
      const outside = root[SizeGuideBraPopup.STORAGE.outsideKey];
      if (!outside?.recommendation) return;
      delete outside.recommendation[SizeGuideBraPopup.STORAGE.guideType];
      this._writeRoot(root);
    }

    _compute (back, chest) {
      const backGroup = SizeGuideBraPopup.BACK_GROUPS.find((g) => back >= g.min && back <= g.max);
      if (!backGroup) return null;

      const diff = chest - back;
      const bonnet = SizeGuideBraPopup.BONNET_THRESHOLDS.find((b) => diff >= b.min && diff <= b.max);
      if (!bonnet) return null;

      return `${backGroup.size} ${bonnet.size}`;
    }

    /* ─── Apply to variant pickers ──────────────────────────────────────── */

    // Programmatically check the option whose label matches the computed
    // size, in EVERY popover on the page (drawer's own + the main variant
    // picker outside it). Then dispatch input + change so the variant picker
    // reacts (URL update, price refresh, etc.). The `_restoring` flag tells
    // `<size-guide-bra>` that the change came from a sibling guide so it
    // doesn't wipe its own state in `_onVariantChange`.
    _applyRecommendation (size) {
      const wasRestoring = window.SizeGuideBase?._restoring;
      if (window.SizeGuideBase) window.SizeGuideBase._restoring = true;

      let matched = false;
      document.querySelectorAll(SizeGuideBraPopup.SELECTORS.optionValue).forEach((option) => {
        if (option.textContent.trim() !== size) return;
        const radio = option.querySelector('input[type="radio"]');
        if (!radio) return;
        matched = true;
        if (radio.checked) return;
        radio.checked = true;
        radio.dispatchEvent(new Event(SizeGuideBraPopup.EVENTS.input, { bubbles: true }));
        radio.dispatchEvent(new Event(SizeGuideBraPopup.EVENTS.change, { bubbles: true }));
      });

      if (matched) this._persistRecommendation(size);

      if (window.SizeGuideBase && !wasRestoring) {
        setTimeout(() => { window.SizeGuideBase._restoring = false; }, SizeGuideBraPopup.RESTORE_WINDOW_MS);
      }

      return matched;
    }

    /* ─── Helpers ────────────────────────────────────────────────────────── */

    _toInt (value) {
      const n = parseInt(value, 10);
      return Number.isFinite(n) ? n : NaN;
    }
  }

  if (!customElements.get('size-guide-bra-popup')) {
    customElements.define('size-guide-bra-popup', SizeGuideBraPopup);
  }
}
